/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, Container, Flex} from '@chakra-ui/react'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerBaskets,
    useCustomerId,
    useCustomerType
} from '@salesforce/commerce-sdk-react'

import useNavigation from '../../hooks/use-navigation'
import Seo from '../../components/seo'
import {useForm} from 'react-hook-form'
import {useRouteMatch} from 'react-router'
import {useLocation} from 'react-router-dom'
import useEinstein from '../../hooks/use-einstein'
import LoginForm from '../../components/login'
import PasswordlessEmailConfirmation from '../../components/email-confirmation'
import {useCustomerActivation} from '../../hooks/useScapiCustomApi'
import {
    ACCOUNT_HREF,
    API_ERROR_MESSAGE,
    CREATE_ACCOUNT_FIRST_ERROR_MESSAGE,
    FEATURE_UNAVAILABLE_ERROR_MESSAGE,
    HYBRID_ACCOUNT,
    HYBRID_AUTH,
    INVALID_TOKEN_ERROR,
    INVALID_TOKEN_ERROR_MESSAGE,
    LOGIN_TYPES,
    PASSWORDLESS_ERROR_MESSAGES,
    PASSWORDLESS_LOGIN_LANDING_PATH,
    TOAST_MESSAGE_MERGE_WISHLIST,
    CUSTOMER_ACTIVATION_TOKEN_QUERY_KEY,
    USER_NOT_FOUND_ERROR,
    PASSWORDLESS_LOGIN_TOKEN_QUERY_KEY
} from '../../constants'
import {isServer} from '../../utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import useMultiSite from '../../hooks/use-multi-site'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import useInteractionStudioPixel from '../../tracking/interaction-studio'
import useHybridURL from '../../hooks/use-hybrid-url'
import {useWishList, useMergeWishlistMutation} from '../../hooks/use-wish-list'
import useOcapiSite from '../../hooks/useOcapiSite'
import {useToast} from '../../hooks/use-toast'
import useEffectEvent from '../../hooks/use-effect-event'
import {trackEvent} from '../../tracking/analytics'
import HeaderContent from '../../components/header-content'
import CMSContent from '../../components/cms-content'
import {getParameterValue} from '../../utils/url'
import useCmsContent from '../../components/cms-content/hooks/use-cms-content'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

const LOGIN_VIEW = 'login'
const EMAIL_VIEW = 'email'

const Login = ({initialView = LOGIN_VIEW}) => {
    const {formatMessage} = useIntl()

    // we use the deprecated useNavigation for the case where we have return page url without page type
    // noinspection JSDeprecatedSymbols
    const navigate = useNavigation()
    const navigateHybrid = useHybridURL()
    const form = useForm()
    const location = useLocation()
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])
    const {path} = useRouteMatch()
    const einstein = useEinstein()
    const {isRegistered} = useCustomerType()
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const loginPasswordless = useAuthHelper(AuthHelpers.LoginPasswordlessUser)
    const authorizePasswordlessLogin = useAuthHelper(AuthHelpers.AuthorizePasswordless)
    const {passwordless = {}, social = {}} = getConfig().app.login || {}
    const isPasswordlessEnabled = !!passwordless?.enabled
    const isSocialEnabled = !!social?.enabled
    const idps = social?.idps
    const {wishlistEnabled} = useOcapiSite().preferences
    const {mutate: mergeWishlistMutation} = useMergeWishlistMutation(
        {enabled: !!wishlistEnabled},
        {
            onSettled: (data) => {
                if (data) {
                    toast({
                        title: `${formatMessage(TOAST_MESSAGE_MERGE_WISHLIST)}`
                    })
                }
            }
        }
    )
    const {data: wishlist} = useWishList({enabled: !!wishlistEnabled})

    const wishlistProductIds = useMemo(
        () =>
            wishlistEnabled
                ? wishlist?.customerProductListItems?.map((item) => item.productId)
                : [],
        [wishlistEnabled, wishlist?.customerProductListItems]
    )

    // TODO v3: check if this is needed
    const customerId = useCustomerId()
    // const prevAuthType = usePrevious(customerType)
    const {data: _baskets, isSuccess: isSuccessCustomerBaskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer, keepPreviousData: true}
    )
    // const mergeBasket = useShopperBasketsMutation('mergeBasket')
    const [currentView, setCurrentView] = useState(initialView)
    const [passwordlessLoginEmail, setPasswordlessLoginEmail] = useState('')
    const [loginType, setLoginType] = useState(LOGIN_TYPES.PASSWORD)

    const toast = useToast()

    const {locale} = useMultiSite()
    const customerActivationToken = useMemo(
        () => getParameterValue(location, CUSTOMER_ACTIVATION_TOKEN_QUERY_KEY),
        [location]
    )
    useCustomerActivation(
        {parameters: {c_token: customerActivationToken, locale: locale.id}},
        {enabled: !!customerActivationToken}
    )

    useInitialDataLayer({pageType: 'Login'})
    useInteractionStudioPixel()

    /**************** Page Cache ****************/
    const {res} = useServerContext()
    if (res) {
        const cacheConfig = getConfig().app.cache
        if (cacheConfig?.login) {
            res.set('Cache-Control', cacheConfig.login)
        }
    }

    const {data: preloadedContent} = useCmsContent('mystokke-login')

    // TODO v3: check if this is needed
    // const handleMergeBasket = () => {
    //     const hasBasketItem = baskets?.baskets?.[0]?.productItems?.length > 0
    //     // we only want to merge basket when the user is logged in as a recurring user
    //     // only recurring users trigger the login mutation, new user triggers register mutation
    //     // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
    //     // if you change logic here, also change it in login page
    //     const shouldMergeBasket = hasBasketItem && prevAuthType === 'guest'
    //     if (shouldMergeBasket) {
    //         try {
    //             mergeBasket.mutate({
    //                 headers: {
    //                     // This is not required since the request has no body
    //                     // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
    //                     'Content-Type': 'application/json'
    //                 },
    //                 parameters: {
    //                     createDestinationBasket: true
    //                 }
    //             })
    //         } catch (e) {
    //             form.setError('global', {
    //                 type: 'manual',
    //                 message: formatMessage(API_ERROR_MESSAGE)
    //             })
    //         }
    //     }
    // }

    const submitForm = async (data) => {
        form.clearErrors()

        const handlePasswordlessLogin = async (email) => {
            try {
                await authorizePasswordlessLogin.mutateAsync({userid: email})
                setCurrentView(EMAIL_VIEW)
            } catch (error) {
                const message = USER_NOT_FOUND_ERROR.test(error.message)
                    ? formatMessage(CREATE_ACCOUNT_FIRST_ERROR_MESSAGE)
                    : PASSWORDLESS_ERROR_MESSAGES.some((msg) => msg.test(error.message))
                    ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                    : formatMessage(API_ERROR_MESSAGE)
                form.setError('global', {type: 'manual', message})
            }
        }

        return {
            login: async (data) => {
                if (loginType === LOGIN_TYPES.PASSWORD) {
                    const loginResult = login.mutateAsync({
                        username: data.email,
                        password: data.password
                    })
                    const trackEventResult = trackEvent({event: 'login', email: data.email})
                    // wait for tracking and login to complete, as otherwise the user might be redirected before the tracking is sent
                    await Promise.allSettled([loginResult, trackEventResult])

                    if (wishlistEnabled) {
                        mergeWishlistMutation(wishlistProductIds)
                    }

                    // need to resolve the submit promise after setting the error message, or the form
                    // will treat it as successful submission
                    return loginResult.catch((data) => {
                        return data.response.json().then((error) => {
                            const message = /account.user.registration.activationmessage/i.test(
                                error.message
                            )
                                ? formatMessage({
                                      defaultMessage:
                                          'In order to login, you have to activate your account from the link we sent you in an email.',
                                      id: 'account-registration.msg.double_opt_in'
                                  })
                                : // upstream code checks only for unauthorized but our instance returns "Invalid Credentials"
                                /Unauthorized/i.test(error.message) ||
                                  /Invalid Credentials/i.test(error.message)
                                ? formatMessage({
                                      defaultMessage:
                                          'Incorrect username or password, please try again.',
                                      id: 'login_page.error.incorrect_username_or_password'
                                  })
                                : formatMessage(API_ERROR_MESSAGE)
                            form.setError('global', {type: 'manual', message})
                        })
                    })
                } else if (loginType === LOGIN_TYPES.PASSWORDLESS) {
                    setPasswordlessLoginEmail(data.email)
                    await handlePasswordlessLogin(data.email)
                }
            },
            email: async () => {
                await handlePasswordlessLogin(passwordlessLoginEmail)
            }
        }[currentView](data)
    }

    // Handles passwordless login by retrieving the 'token' from the query parameters and
    // executing a passwordless login attempt using the token. The process waits for the
    // customer baskets to be loaded to guarantee proper basket merging.
    const onPasswordlessLogin = useEffectEvent(async () => {
        const pwdlessLoginToken = decodeURIComponent(
            queryParams.get(PASSWORDLESS_LOGIN_TOKEN_QUERY_KEY)
        )
        try {
            await loginPasswordless.mutateAsync({pwdlessLoginToken})
        } catch (e) {
            const errorData = await e.response?.json()
            const message = INVALID_TOKEN_ERROR.test(errorData.message)
                ? formatMessage(INVALID_TOKEN_ERROR_MESSAGE)
                : formatMessage(API_ERROR_MESSAGE)
            form.setError('global', {type: 'manual', message})
        }
    })
    useEffect(() => {
        if (path === PASSWORDLESS_LOGIN_LANDING_PATH && isSuccessCustomerBaskets) {
            onPasswordlessLogin()
        }
    }, [path, isSuccessCustomerBaskets, onPasswordlessLogin])

    // If customer is registered push to account page
    const onCustomerRegistered = useEffectEvent(() => {
        // TODO v3: check if this is needed
        // handleMergeBasket()
        const origin =
            getParameterValue(location, 'origin') || getParameterValue(location, 'redirect_url')
        if (origin) {
            const decodedOrigin = decodeURIComponent(origin)
            navigateHybrid(decodedOrigin, decodedOrigin, {})
        } else if (location?.state?.directedFrom) {
            // we can only have state if we came using router navigation,
            // so it's safe to assume we can go back the same way
            // even if we don't know the page type
            navigate(location.state.directedFrom)
        } else {
            navigateHybrid(HYBRID_ACCOUNT, ACCOUNT_HREF)
        }
    })
    useEffect(() => {
        if (isRegistered) {
            onCustomerRegistered()
        }
    }, [isRegistered, onCustomerRegistered])

    /**************** Einstein ****************/
    const onMount = useEffectEvent(() => einstein.sendViewPage(location.pathname))
    useEffect(() => {
        onMount()
    }, [onMount])

    return (
        <>
            <HeaderContent />
            <Box data-testid="login-page" bg="white" py={[8, 16]}>
                <Seo
                    title={formatMessage({
                        id: 'login.page.title',
                        defaultMessage: 'Login into your Stokke® Account | Stokke® Online Shop'
                    })}
                    description="Customer sign in"
                />
                <Flex
                    flexDirection={{base: 'column', md: 'row'}}
                    gap={{base: 4, lg: 6, xl: 8}}
                    padding={{base: 4, lg: 6, xl: 8}}
                    justifyContent="center"
                >
                    {preloadedContent?.c_body !== '' && (
                        <Container
                            width={{base: '100%', md: '50%'}}
                            maxWidth="50.813rem"
                            padding="0"
                            backgroundColor="stokkeCore.white"
                            height="fit-content"
                            margin="0"
                            // could be removed width new cms module for login/registration
                            sx={{'section>div>div.sk-paragraph-wrapper': {maxWidth: '100%'}}}
                        >
                            <CMSContent
                                id="mystokke-login"
                                content={preloadedContent}
                                showError={false}
                            />
                        </Container>
                    )}
                    <Container
                        paddingTop={16}
                        width={{
                            base: '100%',
                            md: preloadedContent?.c_body !== '' ? '50%' : '100%'
                        }}
                        maxWidth="50.813rem"
                        bg="stokkePink.bright"
                        paddingBottom={14}
                        marginLeft="0"
                        marginRight="0"
                        marginBottom={8}
                        borderRadius="base"
                        backgroundColor="stokkePink.bright"
                    >
                        {!form.formState.isSubmitSuccessful && currentView === LOGIN_VIEW && (
                            <LoginForm
                                form={form}
                                submitForm={submitForm}
                                clickCreateAccount={() =>
                                    navigateHybrid(HYBRID_AUTH, '/registration')
                                }
                                handlePasswordlessLoginClick={() => {
                                    setLoginType(LOGIN_TYPES.PASSWORDLESS)
                                }}
                                handleForgotPasswordClick={() =>
                                    navigateHybrid(HYBRID_AUTH, '/reset-password')
                                }
                                isPasswordlessEnabled={isPasswordlessEnabled}
                                isSocialEnabled={isSocialEnabled}
                                idps={idps}
                                setLoginType={setLoginType}
                                variant="page"
                            />
                        )}
                        {form.formState.isSubmitSuccessful && currentView === EMAIL_VIEW && (
                            <PasswordlessEmailConfirmation
                                form={form}
                                submitForm={submitForm}
                                email={passwordlessLoginEmail}
                            />
                        )}
                    </Container>
                </Flex>
            </Box>
        </>
    )
}

Login.getTemplateName = () => 'login'

Login.propTypes = {
    initialView: PropTypes.oneOf([LOGIN_VIEW, EMAIL_VIEW]),
    match: PropTypes.object
}

export default Login
